import React, { useEffect, useState } from "react";
import axios from "axios";
import { API_BASE_URL } from "../config";
import { Briefcase, FileText, Loader2, Monitor, Paperclip } from "lucide-react";

// Read-only (desktop doc 034 §7). Statements and trade reports are imported by
// the user's desktop agent; this page shows what was saved.

const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
const num = (v, digits = 2) =>
    v === null || v === undefined || v === "" ? "—" : Number(v).toLocaleString(undefined, { maximumFractionDigits: digits });
const STALE_DAYS = 30;

async function openDocument(documentId) {
    // Open the tab first: a window opened after an await is treated as a popup.
    const win = window.open("", "_blank");
    try {
        const { data } = await axios.get(`${API_BASE_URL}/api/portfolios/documents/${documentId}/link/`, auth());
        if (win) win.location.href = data.download_url;
    } catch {
        if (win) win.close();
        alert("Could not open the original document.");
    }
}

function AgeBadge({ days }) {
    const stale = days > STALE_DAYS;
    const label = days === 0 ? "today" : days === 1 ? "1 day old" : `${days} days old`;
    return (
        <span className={`text-xs px-2 py-0.5 rounded-full ${stale ? "bg-amber-50 text-amber-700" : "bg-gray-100 text-gray-600"}`}>
            {label}
        </span>
    );
}

function HoldingsTable({ snapshot }) {
    const rows = snapshot.holdings || [];
    if (!rows.length) return <p className="px-6 py-8 text-sm text-gray-500">No holdings on this statement.</p>;
    return (
        <div className="overflow-x-auto">
            <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wide">
                    <tr>
                        <th className="text-left px-6 py-3 font-medium">Stock</th>
                        <th className="text-right px-4 py-3 font-medium">Quantity</th>
                        <th className="text-right px-4 py-3 font-medium">Avg. cost</th>
                        <th className="text-right px-4 py-3 font-medium">Price on statement</th>
                        <th className="text-right px-6 py-3 font-medium">Value on statement</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                    {rows.map((h) => (
                        <tr key={h.symbol}>
                            <td className="px-6 py-3">
                                <div className="font-medium text-gray-900">{h.symbol.split(":").pop()}</div>
                                <div className="text-xs text-gray-500">{h.name}</div>
                            </td>
                            <td className="text-right px-4 py-3 tabular-nums">{num(h.quantity, 4)}</td>
                            <td className="text-right px-4 py-3 tabular-nums">{num(h.average_cost)}</td>
                            <td className="text-right px-4 py-3 tabular-nums">{num(h.market_price)}</td>
                            <td className="text-right px-6 py-3 tabular-nums">{num(h.market_value)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function Unresolved({ rows }) {
    if (!rows?.length) return null;
    return (
        <div className="mx-6 mb-4 rounded-xl bg-amber-50 border border-amber-100 px-4 py-3 text-sm text-amber-800">
            {rows.length} row{rows.length > 1 ? "s" : ""} on this statement did not match a listed stock and {rows.length > 1 ? "are" : "is"} not shown:{" "}
            {rows.map((r) => r.ticker || r.name).join(", ")}. Ask your desktop agent to fix {rows.length > 1 ? "them" : "it"}.
        </div>
    );
}

function TransactionsTable({ data }) {
    if (!data) return <div className="p-10 text-center"><Loader2 className="animate-spin mx-auto text-gray-400" /></div>;
    if (!data.transactions.length)
        return <p className="px-6 py-8 text-sm text-gray-500">No trades yet. They come from trade confirmations you import with the desktop agent.</p>;
    return (
        <div className="overflow-x-auto">
            <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wide">
                    <tr>
                        <th className="text-left px-6 py-3 font-medium">Date</th>
                        <th className="text-left px-4 py-3 font-medium">Type</th>
                        <th className="text-left px-4 py-3 font-medium">Stock</th>
                        <th className="text-right px-4 py-3 font-medium">Quantity</th>
                        <th className="text-right px-4 py-3 font-medium">Price</th>
                        <th className="text-right px-4 py-3 font-medium">Amount</th>
                        <th className="text-right px-6 py-3 font-medium">Commission</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                    {data.transactions.map((t, i) => (
                        <tr key={i}>
                            <td className="px-6 py-3 tabular-nums">{t.trade_date}</td>
                            <td className={`px-4 py-3 font-medium ${t.side === "BUY" ? "text-green-700" : t.side === "SELL" ? "text-red-700" : "text-gray-700"}`}>{t.side}</td>
                            <td className="px-4 py-3">{t.symbol ? t.symbol.split(":").pop() : "Cash"}</td>
                            <td className="text-right px-4 py-3 tabular-nums">{num(t.quantity, 4)}</td>
                            <td className="text-right px-4 py-3 tabular-nums">{num(t.price)}</td>
                            <td className="text-right px-4 py-3 tabular-nums">{num(t.amount)}</td>
                            <td className="text-right px-6 py-3 tabular-nums">{num(t.commission)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
            {data.total > data.transactions.length && (
                <p className="px-6 py-3 text-xs text-gray-500">Showing the latest {data.transactions.length} of {data.total} trades.</p>
            )}
        </div>
    );
}

function AccountView({ account }) {
    const [tab, setTab] = useState("holdings");
    const [dates, setDates] = useState(null);
    const [asOf, setAsOf] = useState(account.latest_snapshot?.as_of || null);
    const [snapshot, setSnapshot] = useState(account.latest_snapshot);
    const [trades, setTrades] = useState(null);

    useEffect(() => {
        setTab("holdings");
        setAsOf(account.latest_snapshot?.as_of || null);
        setSnapshot(account.latest_snapshot);
        setTrades(null);
        setDates(null);
        axios.get(`${API_BASE_URL}/api/portfolios/${account.id}/history/`, auth())
            .then(({ data }) => setDates(data.snapshots))
            .catch(() => setDates([]));
    }, [account.id]);

    const pickDate = async (d) => {
        setAsOf(d);
        if (d === account.latest_snapshot?.as_of) return setSnapshot(account.latest_snapshot);
        setSnapshot(null);
        try {
            const { data } = await axios.get(`${API_BASE_URL}/api/portfolios/${account.id}/snapshot/?as_of=${d}`, auth());
            setSnapshot(data);
        } catch {
            setSnapshot({ holdings: [], unresolved: [] });
        }
    };

    const showTrades = async () => {
        setTab("trades");
        if (trades) return;
        try {
            const { data } = await axios.get(`${API_BASE_URL}/api/portfolios/${account.id}/transactions/`, auth());
            setTrades(data);
        } catch {
            setTrades({ transactions: [], total: 0 });
        }
    };

    return (
        <div>
            <div className="px-6 py-4 flex flex-wrap items-center gap-3 border-b border-gray-100">
                <div className="flex gap-1 bg-gray-100 rounded-lg p-1 text-sm">
                    <button onClick={() => setTab("holdings")} className={`px-3 py-1 rounded-md ${tab === "holdings" ? "bg-white shadow-sm text-gray-900" : "text-gray-500"}`}>Holdings</button>
                    <button onClick={showTrades} className={`px-3 py-1 rounded-md ${tab === "trades" ? "bg-white shadow-sm text-gray-900" : "text-gray-500"}`}>Trades</button>
                </div>
                {tab === "holdings" && asOf && (
                    <>
                        <label className="text-sm text-gray-500 flex items-center gap-2">
                            Statement of
                            <select value={asOf} onChange={(e) => pickDate(e.target.value)} className="border border-gray-200 rounded-lg px-2 py-1 text-sm text-gray-900 bg-white">
                                {(dates || [{ as_of: asOf }]).map((s) => (
                                    <option key={s.as_of} value={s.as_of}>{s.as_of}</option>
                                ))}
                            </select>
                        </label>
                        {snapshot && <AgeBadge days={snapshot.days_old} />}
                        {snapshot?.document_id && (
                            <button onClick={() => openDocument(snapshot.document_id)} className="ml-auto flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800">
                                <FileText size={15} /> View original
                            </button>
                        )}
                    </>
                )}
            </div>
            {tab === "trades" ? (
                <TransactionsTable data={trades} />
            ) : !snapshot ? (
                asOf ? <div className="p-10 text-center"><Loader2 className="animate-spin mx-auto text-gray-400" /></div>
                     : <p className="px-6 py-8 text-sm text-gray-500">No statement imported for this account yet, only trades.</p>
            ) : (
                <>
                    {snapshot.cash_balance !== null && snapshot.cash_balance !== undefined && (
                        <p className="px-6 pt-4 text-sm text-gray-600">Cash on statement: <span className="font-medium tabular-nums">{num(snapshot.cash_balance)}</span></p>
                    )}
                    <div className="pt-4"><Unresolved rows={snapshot.unresolved} /></div>
                    <HoldingsTable snapshot={snapshot} />
                </>
            )}
        </div>
    );
}

export default function PortfolioPage() {
    const [accounts, setAccounts] = useState(null);
    const [error, setError] = useState(null);
    const [selectedId, setSelectedId] = useState(null);

    useEffect(() => {
        axios.get(`${API_BASE_URL}/api/portfolios/`, auth())
            .then(({ data }) => {
                setAccounts(data.accounts);
                const first = data.accounts.find((a) => a.status === "open") || data.accounts[0];
                setSelectedId(first?.id || null);
            })
            .catch(() => setError("Could not load your portfolios."));
    }, []);

    const open = (accounts || []).filter((a) => a.status === "open");
    const closed = (accounts || []).filter((a) => a.status === "closed");
    const selected = (accounts || []).find((a) => a.id === selectedId);

    return (
        <div className="p-4 sm:p-8 max-w-7xl mx-auto">
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                    <Briefcase className="text-blue-600" /> My portfolios
                </h1>
                <p className="text-gray-500 mt-1 text-sm">
                    Imported from your broker statements and trade confirmations by your desktop agent.
                </p>
            </div>

            {error && <p className="text-sm text-red-600">{error}</p>}
            {!accounts && !error && (
                <div className="p-20 text-center"><Loader2 className="animate-spin mx-auto text-gray-400" /></div>
            )}

            {accounts && accounts.length === 0 && (
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-10 text-center">
                    <Monitor className="mx-auto text-gray-300 mb-4" size={48} />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">No portfolios yet</h3>
                    <p className="text-gray-500 max-w-md mx-auto text-sm leading-relaxed">
                        In the AnalystBro desktop app, click <Paperclip size={14} className="inline -mt-0.5" /> to attach your
                        statements or a whole folder of them (PDFs, Excel files, screenshots, from any broker), then choose
                        <span className="font-medium text-gray-700"> Import statements</span> from the + menu.
                    </p>
                </div>
            )}

            {accounts && accounts.length > 0 && (
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                    <div className="flex overflow-x-auto border-b border-gray-100 bg-gray-50/50">
                        {[...open, ...closed].map((a) => (
                            <button
                                key={a.id}
                                onClick={() => setSelectedId(a.id)}
                                className={`px-5 py-3 text-left whitespace-nowrap border-b-2 ${selectedId === a.id ? "border-blue-600 bg-white" : "border-transparent hover:bg-white/60"}`}
                            >
                                <div className={`text-sm font-medium ${selectedId === a.id ? "text-blue-700" : "text-gray-700"}`}>
                                    {a.broker} · {a.account_code}
                                </div>
                                <div className="text-xs text-gray-500 flex items-center gap-1.5">
                                    {a.status === "closed" ? "Closed" : a.latest_snapshot ? `as of ${a.latest_snapshot.as_of}` : "no statement yet"}
                                    {a.stale && <span className="text-amber-600">· may be out of date</span>}
                                </div>
                            </button>
                        ))}
                    </div>
                    {selected?.status === "closed" && (
                        <p className="px-6 pt-4 text-sm text-gray-500">
                            Marked closed{selected.closed_at ? ` on ${selected.closed_at}` : ""}. Your agent leaves it out of advice.
                        </p>
                    )}
                    {selected && <AccountView account={selected} />}
                </div>
            )}
        </div>
    );
}
